// @flow
import { getJson, postJson, putJson, del } from 'utils/http';
import {
  LISTING_TYPE_URL_SEGMENT_MAP,
  LISTING_TYPE_URL_SEGMENT_MAP_SUPPORT_CASE
} from 'utils/constants';
import type { BillingAddress, UserMini, Address } from 'api/shared';
import { pathWithParams } from 'utils/url';

export type Flag = {
  id: string,
  created: string,
  updated: string,
  ipAddress: string,
  byUser: ?{
    id: string,
    email: string,
    name: string
  },
  email: string,
  reason: string,
  text: string
};

export type GradFair = {
  year: number,
  locations: Array<{
    id: string,
    name: string
  }>
};

export type PendingPayment = {
  id: string,
  created: string,
  method: string,
  amount: string,
  user: UserMini,
  last4?: string
};

export type PaymentPart = {
  id: string,
  created: string,
  deleted: string,
  amount: number,
  payment: {
    id: string,
    method: string,
    transactionId: string,
    amount: number
  },
  refunds: ?{
    paymentId: string,
    paymentPartId: string
  }
};

export type LineItemMini = {
  id: string,
  itemType: string,
  description: string
};

export type LineItem = {
  id: string,
  itemType: string,
  itemId?: string,
  description: string,
  quantity: number,
  unitPrice: number,
  due: number,
  total: number,
  paymentParts: Array<PaymentPart>
};

export type InvoiceMini = {
  id: string,
  number: number,
  created: string
};

export type Invoice = {
  id: string,
  number: number,
  created: string,
  updated: string,
  closed: string,
  billingAddress: BillingAddress,
  lineItems: Array<LineItem>,
  creator: ?UserMini,
  paymentSettled: boolean,
  orgNote: string,
  vatNumber: ?string,
  due: number,
  totalForInvoice: string,
  acceptsPartialPayment: boolean,
  publicUrl: string,
  org: {
    id: string,
    name: string
  }
};

export type Chart = {
  name: string,
  url: string,
  height: number
};

export type ActionLogEntry = {
  id: string,
  created: string,
  entry: Object,
  action: string,
  object: {
    id: string,
    type: string,
    name: string,
    stillExists: boolean
  },
  byUser: ?{
    id: string,
    email: string,
    name: string
  },
  byApiToken: ?{
    id: string
  },
  aboutOrg: ?{
    id: string,
    name: string
  },
  aboutGroup: ?{
    id: string,
    name: string
  }
};

export type ActionLogEntryApiResponse = {
  actionLogEntries: Array<ActionLogEntry>,
  hasMore: boolean
};

export type PaymentMini = {
  id: string,
  created: string,
  method: string,
  amount: number,
  transactionId: string,
  last4?: string
};

export type PaymentPaymentPart = {
  id: string,
  created: string,
  amount: number,
  refundableAmount: number,
  org: {
    id: string,
    name: string
  },
  lineItem: LineItemMini,
  invoice: InvoiceMini,
  refunds: Array<PaymentPart>
};

export type Payment = {
  id: string,
  created: string,
  deleted: ?string,
  deletedBy: ?UserMini,
  canBeDeleted: boolean,
  canBeRefunded: boolean,
  method: string,
  transactionId: string,
  amount: number,
  last4?: string,
  refundsPayment: ?PaymentMini,
  refundedByPayments: Array<PaymentMini>,
  user: ?UserMini,
  parts: Array<PaymentPaymentPart>
};

export type CreditPack = {
  id: string,
  created: string,
  packId: string,
  invoice: ?{
    id: string,
    number: string,
    paymentSettled: boolean
  }
};

export type Credit = {
  id: string,
  isUsed: boolean,
  isCancelled: boolean,
  invoice: ?{
    id: string,
    number: string
  },
  listing: ?{
    id?: string,
    name: string,
    listingType: string
  },
  usedBy: ?{
    id?: string,
    name?: string,
    email?: string,
    type: string
  },
  usedOn: ?string,
  usedByStaff: boolean
};

export type AddressMapping = {
  id: string,
  fromKey: string,
  addresses: Array<Address>
};

export const fetchStaffUsers = (): Promise<Array<UserMini>> =>
  getJson(`/data/staff/users/staff`).then(({ staff }) => staff);

export const fetchCaseReasons = (): Promise<Object> =>
  getJson('/data/staff/case-reasons').then(({ reasons }) => reasons);

export const freezeListing = (
  listingType:
    | 'JOB'
    | 'VOLOP'
    | 'INTERNSHIP'
    | 'ORG'
    | 'GROUP'
    | 'IDEALIST_DAY_EVENT'
    | 'USER',
  listingId: string,
  reasons: ?Array<string>,
  details: string
): Promise<void> =>
  postJson(
    `/data/staff/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/freeze`,
    { reasons, details }
  );

export const unfreezeListing = (
  listingType:
    | 'JOB'
    | 'VOLOP'
    | 'INTERNSHIP'
    | 'ORG'
    | 'GROUP'
    | 'IDEALIST_DAY_EVENT'
    | 'USER',
  listingId: string
): Promise<void> =>
  postJson(
    `/data/staff/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/unfreeze`
  );

export const createSupportCase = ({
  listingType,
  listingId,
  reason,
  queueItemId
}: {
  listingType:
    | 'JOB'
    | 'VOLOP'
    | 'INTERNSHIP'
    | 'ORG'
    | 'GROUP'
    | 'IDEALIST_DAY_EVENT'
    | 'IDEALIST_DAYS_MAP_PIN',
  listingId: string,
  reason: string,
  queueItemId: ?string
}): Promise<void> =>
  postJson(
    `/data/staff/${
      LISTING_TYPE_URL_SEGMENT_MAP_SUPPORT_CASE[listingType]
    }/${listingId}/create-support-case`,
    { reason, queueItemId }
  );

export const fetchExternalReportsMetadata = () =>
  getJson('/data/staff/reports/external').then(({ charts }) => charts);

export const fetchHistory = (
  objectType: string,
  objectId: string,
  before?: string
): Promise<ActionLogEntryApiResponse> =>
  getJson(
    pathWithParams('/data/staff/action-log-entries', {
      objectType,
      objectId,
      before
    })
  );

export function fetchInvoices({
  number,
  outstanding
}: {
  number: string,
  outstanding: boolean
}): Promise<Array<Invoice>> {
  return getJson(
    `/data/staff/invoices?number=${number}&outstanding=${outstanding.toString()}`
  ).then(({ invoices }) => invoices);
}

export function fetchInvoice(invoiceId: string): Promise<Invoice> {
  return getJson(`/data/staff/invoices/${invoiceId}`).then(
    ({ invoice }) => invoice
  );
}

export const voidLineItem = (lineItemId: string) =>
  postJson('/data/staff/void-line-items', { lineItemIds: [lineItemId] });

export const useCreditForLineItem = (lineItemId: string) =>
  postJson('/data/staff/use-credits', { lineItemIds: [lineItemId] });

export const allowPartialPayments = (invoiceId: string, allow: boolean) => {
  const url = allow
    ? `/data/staff/invoices/${invoiceId}/allow-partial-payments`
    : `/data/staff/invoices/${invoiceId}/disallow-partial-payments`;
  return postJson(url);
};

export function fetchPayment(paymentId: string): Promise<Payment> {
  return getJson(`/data/staff/payments/${paymentId}`).then(
    ({ payment }) => payment
  );
}

export function revertPayment(paymentId: string): Promise<void> {
  return del(`/data/staff/payments/${paymentId}`);
}

export function refundPayment({
  paymentId,
  andVoid,
  parts
}: {
  paymentId: string,
  andVoid: boolean,
  parts: Array<{ id: string, amount: number }>
}): Promise<void> {
  return postJson(`/data/staff/payments/${paymentId}/refund`, {
    andVoid,
    parts
  });
}

export function createPayment({
  method,
  transactionId,
  lineItemIds,
  amount
}: {
  method: string,
  transactionId: string,
  lineItemIds: Array<string>,
  amount: string
}): Promise<Payment> {
  return postJson('/data/staff/payments', {
    method,
    transactionId,
    lineItemIds,
    amount
  }).then(({ payment }) => payment);
}

export const fetchCreditPack = (
  orgId: string,
  packId: string
): Promise<{ pack: CreditPack, credits: Array<Credit> }> =>
  getJson(`/data/staff/credits/${packId}`);

export const cancelCredits = (creditIds: Array<string>) =>
  putJson(`/data/staff/credits`, { creditIds });

export const fetchActions = (
  params: Object,
  before?: string
): Promise<ActionLogEntryApiResponse> =>
  getJson(
    pathWithParams('/data/staff/action-log-entries', {
      ...params,
      before
    })
  );
